import { Risk, Asset, SoAControl, Policy, AuditResult, Incident, CustomChartConfig } from '../types/iso27001';
import { initialRisks, initialAssets, initialSoA, initialPolicies, initialAuditResults, initialIncidents } from './mockData';

const STORAGE_KEYS = {
  RISKS: 'iso27001_risks',
  ASSETS: 'iso27001_assets',
  SOA: 'iso27001_soa',
  POLICIES: 'iso27001_policies',
  AUDITS: 'iso27001_audits',
  INCIDENTS: 'iso27001_incidents',
  CUSTOM_CHARTS: 'iso27001_custom_charts',
};

export const storageService = {
  // Risks
  getRisks: (): Risk[] => {
    const data = localStorage.getItem(STORAGE_KEYS.RISKS);
    return data ? JSON.parse(data) : initialRisks;
  },
  saveRisks: (risks: Risk[]) => {
    localStorage.setItem(STORAGE_KEYS.RISKS, JSON.stringify(risks));
  },
  addRisk: (risk: Risk) => {
    const risks = storageService.getRisks();
    risks.push(risk);
    storageService.saveRisks(risks);
  },
  updateRisk: (updatedRisk: Risk) => {
    const risks = storageService.getRisks();
    const index = risks.findIndex(r => r.id === updatedRisk.id);
    if (index !== -1) {
      risks[index] = updatedRisk;
      storageService.saveRisks(risks);
    }
  },
  deleteRisk: (id: string) => {
    const risks = storageService.getRisks();
    const filtered = risks.filter(r => r.id !== id);
    storageService.saveRisks(filtered);
  },

  // Assets
  getAssets: (): Asset[] => {
    const data = localStorage.getItem(STORAGE_KEYS.ASSETS);
    return data ? JSON.parse(data) : initialAssets;
  },
  saveAssets: (assets: Asset[]) => {
    localStorage.setItem(STORAGE_KEYS.ASSETS, JSON.stringify(assets));
  },
  addAsset: (asset: Asset) => {
    const assets = storageService.getAssets();
    assets.push(asset);
    storageService.saveAssets(assets);
  },
  updateAsset: (updatedAsset: Asset) => {
    const assets = storageService.getAssets();
    const index = assets.findIndex(a => a.id === updatedAsset.id);
    if (index !== -1) {
      assets[index] = updatedAsset;
      storageService.saveAssets(assets);
    }
  },
  deleteAsset: (id: string) => {
    const assets = storageService.getAssets();
    const filtered = assets.filter(a => a.id !== id);
    storageService.saveAssets(filtered);
  },

  // SoA
  getSoA: (): SoAControl[] => {
    const data = localStorage.getItem(STORAGE_KEYS.SOA);
    const controls: SoAControl[] = data ? JSON.parse(data) : initialSoA;
    
    // Dynamically map policies to controls
    // We need to get policies directly from localStorage to avoid circular dependency if getPolicies called getSoA (it doesn't, but good practice)
    const policiesData = localStorage.getItem(STORAGE_KEYS.POLICIES);
    const policies: Policy[] = policiesData ? JSON.parse(policiesData) : initialPolicies;

    controls.forEach(control => {
      const autoLinked = policies
        .filter(p => p.coveredControls?.includes(control.id))
        .map(p => p.title);
      
      const manualLinked = policies
        .filter(p => control.linkedPolicyIds?.includes(p.id))
        .map(p => p.title);

      // Merge and deduplicate
      control.coveredByPolicies = Array.from(new Set([...autoLinked, ...manualLinked]));
    });
    
    return controls;
  },
  saveSoA: (soa: SoAControl[]) => {
    localStorage.setItem(STORAGE_KEYS.SOA, JSON.stringify(soa));
  },

  // Policies
  getPolicies: (): Policy[] => {
    const data = localStorage.getItem(STORAGE_KEYS.POLICIES);
    return data ? JSON.parse(data) : initialPolicies;
  },
  savePolicies: (policies: Policy[]) => {
    localStorage.setItem(STORAGE_KEYS.POLICIES, JSON.stringify(policies));
  },
  addPolicy: (policy: Policy) => {
    const policies = storageService.getPolicies();
    policies.push(policy);
    storageService.savePolicies(policies);
  },
  updatePolicy: (updatedPolicy: Policy) => {
    const policies = storageService.getPolicies();
    const index = policies.findIndex(p => p.id === updatedPolicy.id);
    if (index !== -1) {
      policies[index] = updatedPolicy;
      storageService.savePolicies(policies);
    }
  },
  deletePolicy: (id: string) => {
    const policies = storageService.getPolicies();
    const filtered = policies.filter(p => p.id !== id);
    storageService.savePolicies(filtered);
  },

  // Audits
  getAuditResults: (): AuditResult[] => {
    const data = localStorage.getItem(STORAGE_KEYS.AUDITS);
    return data ? JSON.parse(data) : initialAuditResults;
  },
  saveAuditResult: (result: AuditResult) => {
    const results = storageService.getAuditResults();
    results.push(result);
    localStorage.setItem(STORAGE_KEYS.AUDITS, JSON.stringify(results));
  },
  updateAuditResult: (updatedResult: AuditResult) => {
    const results = storageService.getAuditResults();
    const index = results.findIndex(r => r.id === updatedResult.id);
    if (index !== -1) {
      results[index] = updatedResult;
      localStorage.setItem(STORAGE_KEYS.AUDITS, JSON.stringify(results));
    }
  },

  // Incidents
  getIncidents: (): Incident[] => {
    const data = localStorage.getItem(STORAGE_KEYS.INCIDENTS);
    return data ? JSON.parse(data) : initialIncidents;
  },
  saveIncidents: (incidents: Incident[]) => {
    localStorage.setItem(STORAGE_KEYS.INCIDENTS, JSON.stringify(incidents));
  },
  addIncident: (incident: Incident) => {
    const incidents = storageService.getIncidents();
    incidents.push(incident);
    storageService.saveIncidents(incidents);
  },
  updateIncident: (updatedIncident: Incident) => {
    const incidents = storageService.getIncidents();
    const index = incidents.findIndex(i => i.id === updatedIncident.id);
    if (index !== -1) {
      incidents[index] = updatedIncident;
      storageService.saveIncidents(incidents);
    }
  },
  deleteIncident: (id: string) => {
    const incidents = storageService.getIncidents();
    const filtered = incidents.filter(i => i.id !== id);
    storageService.saveIncidents(filtered);
  },

  // Helper for Cross-Linking
  getOpenFindingsForItem: (itemId: string): import('../types/iso27001').AuditFinding[] => {
    const audits = storageService.getAuditResults();
    const findings: import('../types/iso27001').AuditFinding[] = [];
    audits.forEach(audit => {
      audit.findings.forEach(finding => {
        if (finding.status === 'open' && finding.relatedItemId === itemId) {
          findings.push(finding);
        }
      });
    });
    return findings;
  },

  // Custom Charts
  getCustomCharts: (): CustomChartConfig[] => {
    const data = localStorage.getItem(STORAGE_KEYS.CUSTOM_CHARTS);
    return data ? JSON.parse(data) : [];
  },
  saveCustomCharts: (charts: CustomChartConfig[]) => {
    localStorage.setItem(STORAGE_KEYS.CUSTOM_CHARTS, JSON.stringify(charts));
  },
  addCustomChart: (chart: CustomChartConfig) => {
    const charts = storageService.getCustomCharts();
    charts.push(chart);
    storageService.saveCustomCharts(charts);
  },
  updateCustomChart: (updatedChart: CustomChartConfig) => {
    const charts = storageService.getCustomCharts();
    const index = charts.findIndex(c => c.id === updatedChart.id);
    if (index !== -1) {
      charts[index] = updatedChart;
      storageService.saveCustomCharts(charts);
    }
  },
  deleteCustomChart: (id: string) => {
    const charts = storageService.getCustomCharts();
    const filtered = charts.filter(c => c.id !== id);
    storageService.saveCustomCharts(filtered);
  },
};
